import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, X } from 'lucide-react';
import { subscribeToNotifications, markAsRead } from '../utils/notificationUtils';
import { useNavigate } from 'react-router-dom';

export default function NotificationBell({ user, companyId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const ref = useRef();

  useEffect(() => {
    if (!companyId || !user?.uid) return;
    const unsub = subscribeToNotifications(companyId, user.uid, (notifs) => {
      setNotifications(notifs);
    });
    return unsub;
  }, [companyId, user?.uid]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const unread = notifications.filter(n => !n.read);
  const recent = notifications.slice(0, 8);

  const handleMarkRead = async (notifId) => {
    try { await markAsRead(companyId, notifId); } catch {}
  };

  const handleViewAll = () => {
    setShowDropdown(false);
    navigate('/notifications');
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 rounded-lg hover:bg-white/10 transition text-gray-300 hover:text-white"
        title={t('notifications.title', 'Notifications')}
      >
        <Bell size={20} />
        {unread.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1 animate-pulse">
            {unread.length > 99 ? '99+' : unread.length}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 top-12 w-80 max-h-[450px] bg-slate-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h3 className="text-sm font-semibold text-white">{t('notifications.title', 'Notifications')}</h3>
            <div className="flex items-center gap-2">
              {unread.length > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded-full">{unread.length} new</span>
              )}
              <button onClick={() => setShowDropdown(false)} className="text-gray-400 hover:text-white">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Notification list */}
          <div className="max-h-[350px] overflow-y-auto">
            {recent.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                <Bell size={24} className="mx-auto mb-2 opacity-50" />
                <p>{t('notifications.empty', 'No notifications')}</p>
              </div>
            ) : (
              recent.map(n => (
                <div
                  key={n.id}
                  onClick={() => handleMarkRead(n.id)}
                  className={`px-4 py-3 border-b border-white/5 cursor-pointer transition hover:bg-white/5 ${!n.read ? 'bg-primary/5 border-l-2 border-l-primary' : ''}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 w-2 h-2 bg-primary rounded-full flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${!n.read ? 'text-white' : 'text-gray-300'}`}>{n.title}</p>
                      <p className="text-xs text-gray-400 mt-0.5 truncate">{n.message}</p>
                      <p className="text-[10px] text-gray-500 mt-1">{formatTimeAgo(n.createdAt)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-white/10 px-4 py-2">
              <button onClick={handleViewAll} className="w-full text-center text-sm text-primary hover:text-primary/80 font-medium py-1">
                {t('notifications.viewAll', 'View All Notifications')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  return date.toLocaleDateString();
}
